import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { publicApi } from '../lib/api';
import { formatCurrency, formatDate, getStatusColor, getStatusLabel } from '../lib/utils';
import { toast } from 'sonner';
import { 
  FileText, 
  CheckCircle, 
  XCircle,
  Clock,
  AlertTriangle,
  Paperclip,
  Download,
  Image,
  Film,
  File
} from 'lucide-react';

export default function PublicPresupuestoPage() {
  const { enlace } = useParams();
  const [presupuesto, setPresupuesto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [comentario, setComentario] = useState('');
  const [respondiendo, setRespondiendo] = useState(false);
  const [adjuntos, setAdjuntos] = useState([]);

  useEffect(() => {
    const loadPresupuesto = async () => {
      try {
        const [presupuestoRes, adjuntosRes] = await Promise.all([
          publicApi.getPresupuesto(enlace),
          publicApi.getAdjuntos(enlace).catch(() => ({ data: [] }))
        ]);
        setPresupuesto(presupuestoRes.data);
        setAdjuntos(adjuntosRes.data || []);
      } catch (error) {
        console.error('Error loading presupuesto:', error);
        setError('Presupuesto no encontrado o enlace inválido');
      } finally {
        setLoading(false);
      }
    };
    loadPresupuesto();
  }, [enlace]);

  const getFileIcon = (tipo) => {
    if (tipo?.startsWith('image/')) return <Image className="w-5 h-5 text-emerald-500" />;
    if (tipo?.startsWith('video/')) return <Film className="w-5 h-5 text-violet-500" />;
    if (tipo === 'application/pdf') return <FileText className="w-5 h-5 text-red-500" />;
    return <File className="w-5 h-5 text-slate-500" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleResponder = async (accion) => {
    setRespondiendo(true);
    try {
      await publicApi.responder(enlace, accion, comentario || null);
      setPresupuesto({ ...presupuesto, estado: accion === 'aprobar' ? 'aprobado' : 'rechazado' });
      toast.success(accion === 'aprobar' ? 'Presupuesto aprobado' : 'Presupuesto rechazado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al responder');
    } finally {
      setRespondiendo(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Presupuesto no encontrado
            </h1>
            <p className="text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isPendiente = presupuesto.estado === 'pendiente';
  const isVencido = presupuesto.estado === 'vencido';

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="public-presupuesto-page">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm border border-slate-200 mb-4">
            <FileText className="w-5 h-5 text-blue-600" />
            <span className="font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              OkPresu
            </span>
          </div>
        </div>

        {/* Status Banner */}
        {!isPendiente && (
          <Card className={`border-2 ${presupuesto.estado === 'aprobado' ? 'border-emerald-200 bg-emerald-50' : presupuesto.estado === 'rechazado' ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50'}`}>
            <CardContent className="p-4 flex items-center justify-center gap-3">
              {presupuesto.estado === 'aprobado' && <CheckCircle className="w-6 h-6 text-emerald-600" />}
              {presupuesto.estado === 'rechazado' && <XCircle className="w-6 h-6 text-red-600" />}
              {isVencido && <Clock className="w-6 h-6 text-amber-600" />}
              <span className={`font-medium ${presupuesto.estado === 'aprobado' ? 'text-emerald-800' : presupuesto.estado === 'rechazado' ? 'text-red-800' : 'text-amber-800'}`}>
                Este presupuesto está {getStatusLabel(presupuesto.estado).toLowerCase()}
              </span>
            </CardContent>
          </Card>
        )}

        {/* Quote */}
        <Card className="shadow-lg overflow-hidden">
          <CardContent className="p-0">
            <div className="quote-preview border-0 shadow-none">
              <div className="quote-header bg-white rounded-t-lg p-4 md:p-6">
                <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                  <div>
                    {/* Logo grande O nombre comercial grande - no ambos */}
                    {(presupuesto.empresa?.logo || presupuesto.empresa?.logo_url) ? (
                      <img 
                        src={presupuesto.empresa.logo_url || `${process.env.REACT_APP_BACKEND_URL}/api/uploads/logos/${presupuesto.empresa.logo}`}
                        alt={presupuesto.empresa?.nombre_comercial || 'Logo'}
                        className="h-12 md:h-16 w-auto object-contain mb-2 md:mb-3"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <h3 
                        className="text-xl md:text-2xl font-bold mb-2 md:mb-3" 
                        style={{ fontFamily: 'Manrope, sans-serif', color: presupuesto.empresa?.color_secundario || '#0F172A' }}
                      >
                        {presupuesto.empresa?.nombre_comercial || 'Empresa'}
                      </h3>
                    )}
                    {presupuesto.empresa?.direccion && (
                      <p className="text-slate-500 text-xs md:text-sm">{presupuesto.empresa.direccion}</p>
                    )}
                    {presupuesto.empresa?.telefono && (
                      <p className="text-slate-500 text-xs md:text-sm">{presupuesto.empresa.telefono}</p>
                    )}
                    {presupuesto.empresa?.cif_nif && (
                      <p className="text-slate-500 text-xs md:text-sm">CIF/NIF: {presupuesto.empresa.cif_nif}</p>
                    )}
                  </div>
                  <div className="text-left md:text-right border-t md:border-t-0 pt-3 md:pt-0">
                    <p className="text-slate-500 text-xs md:text-sm">Presupuesto</p>
                    <p className="font-mono text-base md:text-lg text-slate-900">{presupuesto.numero}</p>
                    <p className="text-slate-500 text-xs md:text-sm mt-1 md:mt-2">
                      {formatDate(presupuesto.created_at)}
                    </p>
                  </div>
                </div>
              </div>
              {/* Línea de color debajo de la cabecera */}
              <div 
                className="h-1"
                style={{ backgroundColor: presupuesto.empresa?.color_primario || '#2563EB' }}
              />
              
              <div className="p-4 md:p-6 border-b border-slate-200 bg-slate-50">
                <p className="text-xs md:text-sm text-slate-500 mb-1">Presupuesto para:</p>
                <p className="font-semibold text-slate-900 text-base md:text-lg">{presupuesto.cliente?.nombre}</p>
                {presupuesto.cliente?.empresa_cliente && (
                  <p className="text-slate-600 text-sm">{presupuesto.cliente.empresa_cliente}</p>
                )}
                {presupuesto.cliente?.cif_nif && (
                  <p className="text-slate-600 text-xs md:text-sm">CIF/NIF: {presupuesto.cliente.cif_nif}</p>
                )}
                {presupuesto.cliente?.email && (
                  <p className="text-slate-600 text-xs md:text-sm">{presupuesto.cliente.email}</p>
                )}
                {presupuesto.cliente?.telefono && (
                  <p className="text-slate-600 text-xs md:text-sm">Tel: {presupuesto.cliente.telefono}</p>
                )}
                {/* Dirección completa del cliente */}
                {(presupuesto.cliente?.direccion || presupuesto.cliente?.ciudad || presupuesto.cliente?.codigo_postal) && (
                  <p className="text-slate-500 text-xs md:text-sm mt-1">
                    {[
                      presupuesto.cliente?.direccion,
                      [presupuesto.cliente?.codigo_postal, presupuesto.cliente?.ciudad].filter(Boolean).join(' '),
                      presupuesto.cliente?.provincia
                    ].filter(Boolean).join(', ')}
                  </p>
                )}
              </div>
              
              {/* Items - Responsive table */}
              <div className="quote-items p-4 md:p-6 overflow-x-auto">
                <table className="w-full min-w-[500px]">
                  <thead>
                    <tr className="border-b-2 border-slate-200">
                      <th className="text-left py-3 text-slate-500 text-xs md:text-sm font-semibold">Concepto</th>
                      <th className="text-center py-3 text-slate-500 text-xs md:text-sm font-semibold w-16">Cant.</th>
                      <th className="text-right py-3 text-slate-500 text-xs md:text-sm font-semibold w-20">Precio</th>
                      <th className="text-right py-3 text-slate-500 text-xs md:text-sm font-semibold w-16">Dto.</th>
                      <th className="text-right py-3 text-slate-500 text-xs md:text-sm font-semibold w-24">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {presupuesto.items?.map((item, index) => (
                      <tr key={index} className="border-b border-slate-100">
                        <td className="py-3 md:py-4 pr-2 text-sm">{item.descripcion}</td>
                        <td className="py-3 md:py-4 text-center text-sm">{item.cantidad}</td>
                        <td className="py-3 md:py-4 text-right text-sm">{formatCurrency(item.precio_unitario)}</td>
                        <td className="py-3 md:py-4 text-right text-emerald-600 text-sm">
                          {item.descuento_aplicado > 0 
                            ? `-${formatCurrency(item.descuento_aplicado)}`
                            : ''
                          }
                        </td>
                        <td className="py-3 md:py-4 text-right font-medium text-sm">{formatCurrency(item.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Totals - Responsive */}
              <div className="quote-footer p-4 md:p-6 bg-slate-50">
                <div className="flex justify-end">
                  <div className="w-full md:w-72 space-y-3">
                    <div className="flex justify-between text-sm md:text-base">
                      <span className="text-slate-500">Subtotal</span>
                      <span className="font-medium">{formatCurrency(presupuesto.subtotal)}</span>
                    </div>
                    {presupuesto.total_descuentos > 0 && (
                      <div className="flex justify-between text-emerald-600 text-sm md:text-base">
                        <span>Descuentos</span>
                        <span className="font-medium">-{formatCurrency(presupuesto.total_descuentos)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm md:text-base">
                      <span className="text-slate-500">IVA (21%)</span>
                      <span className="font-medium">{formatCurrency(presupuesto.iva)}</span>
                    </div>
                    <div className="flex justify-between text-lg md:text-xl font-bold pt-3 border-t-2 border-slate-200">
                      <span>Total</span>
                      <span style={{ color: presupuesto.empresa?.color_primario || '#2563EB' }}>{formatCurrency(presupuesto.total)}</span>
                    </div>
                  </div>
                </div>
                
                <div className="mt-6 md:mt-8 pt-4 md:pt-6 border-t border-slate-200 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500 mb-1">Validez</p>
                    <p className="font-medium">{presupuesto.validez_dias} días desde la fecha de emisión</p>
                  </div>
                  <div>
                    <p className="text-slate-500 mb-1">Forma de pago</p>
                    <p className="font-medium">{presupuesto.forma_pago}</p>
                  </div>
                </div>
                
                {presupuesto.notas && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <p className="text-slate-500 text-sm mb-1">Notas</p>
                    <p className="text-slate-700 text-sm">{presupuesto.notas}</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Adjuntos */}
        {adjuntos.length > 0 && (
          <Card className="shadow-lg">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Paperclip className="w-5 h-5" />
                Archivos Adjuntos ({adjuntos.length})
              </h3>
              <div className="grid gap-3">
                {adjuntos.map(adjunto => {
                  const isImage = adjunto.tipo?.startsWith('image/');
                  const fileUrl = `${process.env.REACT_APP_BACKEND_URL}/api/uploads/adjuntos/${adjunto.nombre_archivo}`;
                  
                  return (
                    <a
                      key={adjunto.id}
                      href={fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                    >
                      {isImage ? (
                        <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 border border-slate-200">
                          <img 
                            src={fileUrl} 
                            alt={adjunto.nombre_original}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </div>
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                          {getFileIcon(adjunto.tipo)}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate text-slate-900">{adjunto.nombre_original}</p>
                        <p className="text-xs text-slate-500">{formatFileSize(adjunto.tamaño)}</p>
                      </div>
                      <Download className="w-4 h-4 text-slate-400" />
                    </a>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Response Form */}
        {isPendiente && (
          <Card className="shadow-lg">
            <CardContent className="p-6 space-y-4">
              <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Responder al presupuesto
              </h3>
              <Textarea
                placeholder="Añade un comentario (opcional)..."
                value={comentario}
                onChange={(e) => setComentario(e.target.value)}
                rows={3}
                data-testid="public-comentario"
              />
              <div className="flex gap-4">
                <Button
                  className="flex-1 h-12 text-white"
                  style={{ backgroundColor: presupuesto.empresa?.color_primario || '#10B981' }}
                  onClick={() => handleResponder('aprobar')}
                  disabled={respondiendo}
                  data-testid="public-aprobar-btn"
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Aprobar Presupuesto
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 text-red-600 border-red-200 hover:bg-red-50 h-12"
                  onClick={() => handleResponder('rechazar')}
                  disabled={respondiendo}
                  data-testid="public-rechazar-btn"
                >
                  <XCircle className="w-5 h-5 mr-2" />
                  Rechazar
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center text-sm text-slate-500 py-4">
          Presupuesto enviado con{' '}
          <a href="/" className="text-blue-600 hover:underline font-medium">
            OkPresu
          </a>
        </div>
      </div>
    </div>
  );
}
